import {
  ConnectorMode,
  PointStyle,
  ShapeType,
  StrokeStyle,
} from '@blocksuite/affine-model';
import * as Y from 'yjs';

import type {
  MermaidGraphEdgeIR,
  MermaidGraphIR,
  MermaidGraphNodeIR,
} from './mermaid-graph-ir.js';

export type MermaidCanvasOrigin = { x: number; y: number };

export type MermaidCanvasShapeSpec = {
  irId: string;
  props: {
    shapeType: ShapeType;
    radius: number;
    text: Y.Text;
    xywh: string;
  };
};

export type MermaidCanvasConnectorSpec = {
  irId: string;
  sourceIrId: string;
  targetIrId: string;
  props: {
    mode: ConnectorMode;
    source: { id: string };
    target: { id: string };
    strokeStyle: StrokeStyle;
    rearEndpointStyle: PointStyle;
    text?: Y.Text;
  };
};

export type MermaidCanvasGroupSpec = {
  irId: string;
  title: string;
  childIrIds: string[];
};

export type MermaidCanvasPlan = {
  shapes: MermaidCanvasShapeSpec[];
  connectors: MermaidCanvasConnectorSpec[];
  groups: MermaidCanvasGroupSpec[];
};

export type MermaidCanvasCrud = {
  addElement(
    type: 'shape' | 'connector' | 'group',
    props: Record<string, unknown>
  ): string | null | undefined;
};

export type MermaidCanvasConversionResult = {
  shapeIds: Record<string, string>;
  connectorIds: Record<string, string>;
  groupIds: Record<string, string>;
};

export type MermaidConversionReport = {
  complete: boolean;
  convertibleNodeIds: string[];
  convertibleEdgeIds: string[];
  unsupported: MermaidGraphIR['unsupported'];
};

export type MermaidCanvasSnapshot = {
  version: 1;
  mode: 'mermaid-to-canvas';
  originalSource: string;
  sourceElementId?: string;
  result: MermaidCanvasConversionResult;
};

const NODE_WIDTH = 180;
const NODE_HEIGHT = 88;
const PRIMARY_GAP = 140;
const SECONDARY_GAP = 80;

function nodeShape(node: MermaidGraphNodeIR): {
  shapeType: ShapeType;
  radius: number;
} {
  switch (node.shape) {
    case 'ellipse':
      return { shapeType: ShapeType.Ellipse, radius: 0 };
    case 'diamond':
      return { shapeType: ShapeType.Diamond, radius: 0 };
    case 'roundedRect':
      return { shapeType: ShapeType.Rect, radius: 0.1 };
    default:
      return { shapeType: ShapeType.Rect, radius: 0 };
  }
}

function computeRanks(ir: MermaidGraphIR): Map<string, number> {
  const sourceOrder = new Map(ir.nodes.map((node, index) => [node.id, index]));
  const outgoing = new Map<string, string[]>();
  const indegree = new Map(ir.nodes.map(node => [node.id, 0]));

  for (const edge of ir.edges) {
    if (!indegree.has(edge.source) || !indegree.has(edge.target)) continue;
    const list = outgoing.get(edge.source) ?? [];
    list.push(edge.target);
    outgoing.set(edge.source, list);
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
  }

  const queue = ir.nodes
    .filter(node => (indegree.get(node.id) ?? 0) === 0)
    .map(node => node.id);
  const ranks = new Map<string, number>(queue.map(id => [id, 0]));

  while (queue.length) {
    const id = queue.shift()!;
    const rank = ranks.get(id) ?? 0;
    const targets = [...(outgoing.get(id) ?? [])].sort(
      (a, b) => (sourceOrder.get(a) ?? 0) - (sourceOrder.get(b) ?? 0)
    );

    for (const target of targets) {
      ranks.set(target, Math.max(ranks.get(target) ?? 0, rank + 1));
      indegree.set(target, (indegree.get(target) ?? 1) - 1);
      if ((indegree.get(target) ?? 0) === 0) queue.push(target);
    }
  }

  let fallbackRank = Math.max(0, ...ranks.values());
  for (const node of ir.nodes) {
    if (!ranks.has(node.id)) {
      fallbackRank += 1;
      ranks.set(node.id, fallbackRank);
    }
  }

  return ranks;
}

function layoutNodes(
  ir: MermaidGraphIR,
  origin: MermaidCanvasOrigin
): Map<string, string> {
  const ranks = computeRanks(ir);
  const buckets = new Map<number, string[]>();
  for (const node of ir.nodes) {
    const rank = ranks.get(node.id) ?? 0;
    const list = buckets.get(rank) ?? [];
    list.push(node.id);
    buckets.set(rank, list);
  }

  const vertical =
    ir.direction === 'TB' || ir.direction === 'TD' || ir.direction === 'BT';
  const reverse = ir.direction === 'BT' || ir.direction === 'RL';
  const maxRank = Math.max(0, ...buckets.keys());
  const xywh = new Map<string, string>();

  for (const [rank, ids] of [...buckets.entries()].sort(
    (a, b) => a[0] - b[0]
  )) {
    const displayRank = reverse ? maxRank - rank : rank;
    ids.forEach((id, index) => {
      const primary =
        displayRank *
        (vertical ? NODE_HEIGHT + PRIMARY_GAP : NODE_WIDTH + PRIMARY_GAP);
      const secondary =
        index *
        (vertical ? NODE_WIDTH + SECONDARY_GAP : NODE_HEIGHT + SECONDARY_GAP);
      const x = origin.x + (vertical ? secondary : primary);
      const y = origin.y + (vertical ? primary : secondary);
      xywh.set(id, `[${x},${y},${NODE_WIDTH},${NODE_HEIGHT}]`);
    });
  }

  return xywh;
}

function connectorStyle(edge: MermaidGraphEdgeIR) {
  return {
    mode: ConnectorMode.Orthogonal,
    strokeStyle: edge.line === 'dashed' ? StrokeStyle.Dash : StrokeStyle.Solid,
    rearEndpointStyle:
      edge.arrow === 'forward' ? PointStyle.Arrow : PointStyle.None,
  };
}

export function getMermaidConversionReport(
  ir: MermaidGraphIR
): MermaidConversionReport {
  return {
    complete: ir.unsupported.length === 0,
    convertibleNodeIds: ir.nodes.map(node => node.id),
    convertibleEdgeIds: ir.edges.map(edge => edge.id),
    unsupported: [...ir.unsupported],
  };
}

export function assertMermaidConversionAllowed(
  ir: MermaidGraphIR,
  allowPartial = false
): MermaidConversionReport {
  const report = getMermaidConversionReport(ir);
  if (!report.complete && !allowPartial) {
    throw new Error(
      'Mermaid conversion is partial: ' +
        report.unsupported.length +
        ' unsupported construct(s). Pass allowPartial=true explicitly to continue.'
    );
  }
  return report;
}

export function buildMermaidCanvasPlan(
  ir: MermaidGraphIR,
  origin: MermaidCanvasOrigin = { x: 0, y: 0 }
): MermaidCanvasPlan {
  const positions = layoutNodes(ir, origin);

  return {
    shapes: ir.nodes.map(node => ({
      irId: node.id,
      props: {
        ...nodeShape(node),
        text: new Y.Text(node.label),
        xywh:
          positions.get(node.id) ??
          `[${origin.x},${origin.y},${NODE_WIDTH},${NODE_HEIGHT}]`,
      },
    })),
    connectors: ir.edges.map(edge => ({
      irId: edge.id,
      sourceIrId: edge.source,
      targetIrId: edge.target,
      props: {
        ...connectorStyle(edge),
        source: { id: edge.source },
        target: { id: edge.target },
        ...(edge.label ? { text: new Y.Text(edge.label) } : {}),
      },
    })),
    groups: ir.subgraphs.map(group => ({
      irId: group.id,
      title: group.label,
      childIrIds: [...group.nodeIds],
    })),
  };
}

export function applyMermaidCanvasPlan(
  plan: MermaidCanvasPlan,
  crud: MermaidCanvasCrud
): MermaidCanvasConversionResult {
  const shapeIds: Record<string, string> = {};
  const connectorIds: Record<string, string> = {};
  const groupIds: Record<string, string> = {};

  for (const shape of plan.shapes) {
    const id = crud.addElement('shape', shape.props);
    if (!id)
      throw new Error(`Failed to create shape for Mermaid node ${shape.irId}`);
    shapeIds[shape.irId] = id;
  }

  for (const connector of plan.connectors) {
    const sourceId = shapeIds[connector.sourceIrId];
    const targetId = shapeIds[connector.targetIrId];
    if (!sourceId || !targetId) {
      throw new Error(
        `Missing native endpoint for Mermaid edge ${connector.irId}`
      );
    }
    const id = crud.addElement('connector', {
      ...connector.props,
      source: { id: sourceId },
      target: { id: targetId },
    });
    if (!id)
      throw new Error(
        `Failed to create connector for Mermaid edge ${connector.irId}`
      );
    connectorIds[connector.irId] = id;
  }

  for (const group of plan.groups) {
    const children = Object.fromEntries(
      group.childIrIds
        .map(irId => shapeIds[irId])
        .filter((id): id is string => Boolean(id))
        .map(id => [id, true])
    );
    if (Object.keys(children).length !== group.childIrIds.length) {
      throw new Error(
        `Missing native child for Mermaid subgraph ${group.irId}`
      );
    }
    const id = crud.addElement('group', { children, title: group.title });
    if (!id)
      throw new Error(
        `Failed to create group for Mermaid subgraph ${group.irId}`
      );
    groupIds[group.irId] = id;
  }

  return { shapeIds, connectorIds, groupIds };
}

export type MermaidReverseShapeInput = {
  id: string;
  shapeType: ShapeType;
  radius: number;
  text: string;
};

export type MermaidReverseConnectorInput = {
  id: string;
  sourceId?: string;
  targetId?: string;
  strokeStyle: StrokeStyle;
  rearEndpointStyle: PointStyle;
  text?: string;
};

export type MermaidReverseUnsupported = {
  id: string;
  kind: 'shape' | 'connector';
  reason: string;
};

export type MermaidReverseConversion = {
  complete: boolean;
  source: string;
  unsupported: MermaidReverseUnsupported[];
};

function reverseNodeSyntax(
  alias: string,
  shape: MermaidReverseShapeInput
): string | null {
  const label = shape.text.replace(/"/g, '\"');
  if (shape.shapeType === ShapeType.Rect) {
    return shape.radius > 0 ? `${alias}("${label}")` : `${alias}["${label}"]`;
  }
  if (shape.shapeType === ShapeType.Ellipse) {
    return `${alias}(("${label}"))`;
  }
  if (shape.shapeType === ShapeType.Diamond) {
    return `${alias}{"${label}"}`;
  }
  return null;
}

export function convertCanvasSelectionToMermaid(
  shapes: MermaidReverseShapeInput[],
  connectors: MermaidReverseConnectorInput[],
  options: {
    direction?: MermaidGraphIR['direction'];
    allowPartial?: boolean;
  } = {}
): MermaidReverseConversion {
  const direction = options.direction ?? 'TD';
  const unsupported: MermaidReverseUnsupported[] = [];
  const aliases = new Map<string, string>();
  const nodeLines: string[] = [];

  shapes.forEach((shape, index) => {
    const alias = `N${index + 1}`;
    const syntax = reverseNodeSyntax(alias, shape);
    if (!syntax) {
      unsupported.push({
        id: shape.id,
        kind: 'shape',
        reason: `Shape type ${shape.shapeType} is outside the supported Mermaid subset.`,
      });
      return;
    }
    aliases.set(shape.id, alias);
    nodeLines.push(`  ${syntax}`);
  });

  const edgeLines: string[] = [];
  for (const connector of connectors) {
    if (!connector.sourceId || !connector.targetId) {
      unsupported.push({
        id: connector.id,
        kind: 'connector',
        reason: 'Connector must bind both endpoints to selected shapes.',
      });
      continue;
    }

    const source = aliases.get(connector.sourceId);
    const target = aliases.get(connector.targetId);
    if (!source || !target) {
      unsupported.push({
        id: connector.id,
        kind: 'connector',
        reason: 'Connector endpoint is not a supported selected shape.',
      });
      continue;
    }

    if (
      connector.strokeStyle !== StrokeStyle.Solid &&
      connector.strokeStyle !== StrokeStyle.Dash
    ) {
      unsupported.push({
        id: connector.id,
        kind: 'connector',
        reason: `Stroke style ${connector.strokeStyle} is unsupported.`,
      });
      continue;
    }

    if (
      connector.rearEndpointStyle !== PointStyle.Arrow &&
      connector.rearEndpointStyle !== PointStyle.None
    ) {
      unsupported.push({
        id: connector.id,
        kind: 'connector',
        reason: `Rear endpoint style ${connector.rearEndpointStyle} is unsupported.`,
      });
      continue;
    }

    const dashed = connector.strokeStyle === StrokeStyle.Dash;
    const arrow = connector.rearEndpointStyle === PointStyle.Arrow;
    const operator = dashed ? (arrow ? '-.->' : '-.-') : arrow ? '-->' : '---';
    const label = connector.text?.trim();
    edgeLines.push(
      `  ${source} ${operator}${label ? `|${label.replace(/\|/g, '\\|')}|` : ''} ${target}`
    );
  }

  if (unsupported.length > 0 && !options.allowPartial) {
    return { complete: false, source: '', unsupported };
  }

  return {
    complete: unsupported.length === 0,
    source: [`flowchart ${direction}`, ...nodeLines, ...edgeLines].join('\n'),
    unsupported,
  };
}

export type MermaidCanvasTransaction = {
  transact<T>(fn: () => T): T;
};

export function convertMermaidGraphToCanvasSnapshot(
  input: {
    source: string;
    ir: MermaidGraphIR;
    sourceElementId?: string;
    origin?: MermaidCanvasOrigin;
    allowPartial?: boolean;
  },
  crud: MermaidCanvasCrud
): MermaidCanvasSnapshot {
  assertMermaidConversionAllowed(input.ir, input.allowPartial ?? false);
  const plan = buildMermaidCanvasPlan(input.ir, input.origin);
  const result = applyMermaidCanvasPlan(plan, crud);
  return {
    version: 1,
    mode: 'mermaid-to-canvas',
    originalSource: input.source,
    ...(input.sourceElementId
      ? { sourceElementId: input.sourceElementId }
      : {}),
    result,
  };
}

export function convertMermaidGraphToCanvasSnapshotInTransaction(
  input: Parameters<typeof convertMermaidGraphToCanvasSnapshot>[0],
  crud: MermaidCanvasCrud,
  transaction: MermaidCanvasTransaction
): MermaidCanvasSnapshot {
  return transaction.transact(() =>
    convertMermaidGraphToCanvasSnapshot(input, crud)
  );
}
